import * as THREE from 'three';
import { G, K_BEV, mesh } from '../render/scene';
import { MAT_P } from '../render/materials';
/* ============================================================
   機體造型的共用技法（1:1 移植 `mech_sample_ani.html` §3）
   ============================================================

     bevel  倒角塊。8 段 + thetaStart π/8，四個平面對齊 ±X/±Z，
            只剩四條稜是倒角。w/d 乘 K_BEV 之後與 box 同義
     plate  傾斜裝甲板。tilt 預設 0.16 rad ≈ 9°，`roll` 繞 Z 做斜面
     seam   縫線：夾在板件之間的深色細條，讀起來是一條凹進去的線
     rib    稜線：略為突出的八角細棒，吃 key light，讀起來是一條亮線
     joint  外露的關節。`cap = true` 讓軸端突出到裝甲外（髖／膝／肩用，肘刻意不用）
     ram    液壓桿：粗筒 + 細桿錯開，讀起來像有行程的作動器
     cable  纜束：兩節折線細管，用直的 primitive 湊出垂下來的線束

   `ram` 與 `cable` 是這一版新增的。它們屬於「線條層」——
   60px 的戰場鏡頭下完全看不到，回收全在機庫。

   代價是 primitive 數暴增（起手件全機 151 個）。因為有了
   `mechs.ts` 的 `mergeBones()`（按骨段依材質烘成約 40 個 mesh），
   primitive 數不再等於 draw call，所以這個代價是可以付的。 */

/** 比例。造型本身 1:1，`scale` 只把 demo 的尺度放大到遊戲世界。 */
export var PROP = { scale: 1.18 };

/** 記下這個 builder 認得哪些形狀代號（測試靠它抓打錯的代號）。 */
export function mark(g, shape, known: string[]) {
  (g.userData as any).shape = shape;
  (g.userData as any).known = known;
}

/** 帶倒角的裝甲塊。w/d 量的是面對面寬度，與 box 同義。 */
export function bevel(mat, w, h, d, p, r?) {
  return mesh(G.bev, mat, [w * K_BEV, h, d * K_BEV], p, r);
}
/** 傾斜的裝甲板。tilt 預設 0.16 rad ≈ 9°。`roll` 繞 Z，做斜面用。 */
export function plate(mat, w, h, d, p, tilt?, yaw?, roll?) {
  return mesh(G.box, mat, [w, h, d], p,
    [tilt === undefined ? 0.16 : tilt, yaw || 0, roll || 0]);
}
/** 縫線：夾在板件之間的深色細條。 */
export function seam(w, h, d, p, tilt?, yaw?, roll?) {
  return mesh(G.box, MAT_P.trim, [w, h, d], p, [tilt || 0, yaw || 0, roll || 0]);
}
/** 稜線：略為突出的八角細棒，吃 key light。
    用八邊柱不用 box：box 細棒在某些角度整根變暗，八邊柱轉到哪都有一面吃到光。 */
export function rib(mat, w, h, d, p, tilt?, yaw?, roll?) {
  return mesh(G.bev, mat, [w * K_BEV, h, d * K_BEV], p, [tilt || 0, yaw || 0, roll || 0]);
}
/** 露出來的關節。`cap = true` 時軸端突出到裝甲外（髖／膝／肩用，肘刻意不用）。 */
export function joint(r, p, cap?) {
  var g = new THREE.Group();
  g.position.set(p[0], p[1], p[2]);
  g.add(mesh(G.cyl, MAT_P.trim, [r * 2.1, r * (cap ? 2.5 : 1.5), r * 2.1], [0, 0, 0], [0, 0, Math.PI / 2]));
  g.add(mesh(G.oct, MAT_P.dark, [r * 1.5, r * 1.5, r * 1.5], [0, 0, 0]));
  if (cap) {
    g.add(mesh(G.cyl, MAT_P.dark, [r * 1.35, r * 0.45, r * 1.35], [r * 1.32, 0, 0], [0, 0, Math.PI / 2]));
    g.add(mesh(G.cyl, MAT_P.dark, [r * 1.35, r * 0.45, r * 1.35], [-r * 1.32, 0, 0], [0, 0, Math.PI / 2]));
  }
  return g;
}
/** 液壓桿：粗筒 + 細桿錯開，讀起來像有行程的作動器。 */
export function ram(p, len, a, b, rot?) {
  var g = new THREE.Group();
  g.position.set(p[0], p[1], p[2]);
  if (rot) g.rotation.set(rot[0], rot[1], rot[2]);
  g.add(mesh(G.cyl, MAT_P.trim, [a, len * 0.62, a], [0, len * 0.19, 0]));
  g.add(mesh(G.cyl, MAT_P.armor, [b, len * 0.52, b], [0, -len * 0.24, 0]));
  return g;
}
/** 纜束：兩節折線細管，用直的 primitive 湊出垂下來的線束。 */
export function cable(p, len) {
  var g = new THREE.Group();
  g.position.set(p[0], p[1], p[2]);
  g.add(mesh(G.cyl, MAT_P.trim, [0.075, len, 0.075], [0, -len * 0.44, len * 0.16], [0.58, 0, 0]));
  g.add(mesh(G.cyl, MAT_P.trim, [0.075, len * 0.86, 0.075], [0, -len * 1.14, len * 0.30], [-0.30, 0, 0]));
  return g;
}
